import * as React from 'react'
import * as Toast from '@radix-ui/react-toast'
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Toaster — superfície única de aviso da SPA, sobre @radix-ui/react-toast.
 *
 * Existe porque erro fora do ciclo de render (handler de clique, Promise sem
 * catch, mutação que falha) não passa por error boundary nenhum — a API do
 * React não alcança. Sem esta superfície, essas falhas somem em silêncio.
 *
 * NUNCA passar PII (CPF, nome, documento) para `mostrarAviso` — a mensagem é
 * exibida na tela e enviada para observabilidade.
 */

export type TipoAviso = 'erro' | 'alerta' | 'info' | 'sucesso'

export interface Aviso {
  id: number
  titulo: string
  descricao?: string
  /** Tempo em tela, em ms. Ausente usa o padrão do Provider. */
  duracaoMs?: number
  tipo: TipoAviso
}

const ESTILO_POR_TIPO: Record<TipoAviso, { Icone: LucideIcon; borda: string; icone: string }> = {
  erro: {
    Icone: AlertCircle,
    borda: 'border-danger-200 dark:border-danger-800',
    icone: 'text-danger-700 dark:text-danger-400',
  },
  alerta: {
    Icone: AlertTriangle,
    borda: 'border-warning-300 dark:border-warning-800',
    icone: 'text-warning-700 dark:text-warning-300',
  },
  info: {
    Icone: Info,
    borda: 'border-info-200 dark:border-info-800',
    icone: 'text-info-700 dark:text-info-200',
  },
  sucesso: {
    Icone: CheckCircle2,
    borda: 'border-success-200 dark:border-success-800',
    icone: 'text-success-700 dark:text-success-400',
  },
}

type Assinante = (aviso: Aviso) => void

let proximoId = 1
const assinantes = new Set<Assinante>()

/**
 * mostrarAviso publica um aviso. Chamável de fora do React (listeners globais).
 * `tipo` ausente é 'erro': só ele usa role="alert" e anúncio assertivo.
 */
export function mostrarAviso(titulo: string, descricao?: string, duracaoMs?: number, tipo: TipoAviso = 'erro'): void {
  const aviso: Aviso = { id: proximoId++, titulo, descricao, duracaoMs, tipo }
  assinantes.forEach((assinante) => assinante(aviso))
}

export function Toaster() {
  const [avisos, setAvisos] = React.useState<Aviso[]>([])

  React.useEffect(() => {
    const assinante: Assinante = (aviso) => setAvisos((atuais) => [...atuais, aviso])
    assinantes.add(assinante)
    return () => {
      assinantes.delete(assinante)
    }
  }, [])

  function fechar(id: number) {
    setAvisos((atuais) => atuais.filter((a) => a.id !== id))
  }

  return (
    <Toast.Provider swipeDirection="right" duration={8000}>
      {avisos.map((aviso) => {
        const { Icone, borda, icone } = ESTILO_POR_TIPO[aviso.tipo]
        const erro = aviso.tipo === 'erro'
        return (
          <Toast.Root
            key={aviso.id}
            data-tipo={aviso.tipo}
            role={erro ? 'alert' : 'status'}
            type={erro ? 'foreground' : 'background'}
            duration={aviso.duracaoMs}
            onOpenChange={(aberto) => {
              if (!aberto) fechar(aviso.id)
            }}
            className={cn(
              'flex items-start gap-3 rounded-lg border bg-paper p-4 shadow-lg',
              borda,
              'data-[state=closed]:animate-out data-[state=closed]:fade-out',
            )}
          >
            <Icone className={cn('mt-0.5 h-4 w-4 shrink-0', icone)} aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <Toast.Title className="text-sm font-medium text-ink">{aviso.titulo}</Toast.Title>
              {aviso.descricao && <Toast.Description className="mt-0.5 text-xs text-muted">{aviso.descricao}</Toast.Description>}
            </div>
            <Toast.Close
              aria-label="Fechar aviso"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted hover:text-ink"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Toast.Close>
          </Toast.Root>
        )
      })}
      {/* z-[60] fica acima de qualquer Dialog/Sheet (z-50 no conteúdo, z-40 no
          overlay). Empatado em z-50 quem ganhava era o modal, por vir depois no
          DOM (Radix portala no fim do body, o Toaster monta no App): o retorno
          de sucesso/erro de uma ação feita DENTRO do modal ficava escondido. */}
      <Toast.Viewport className="fixed bottom-20 right-4 z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 outline-none lg:bottom-4" />
    </Toast.Provider>
  )
}
